import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { uploadOnePhoto } from '../../utils/uploadPhotos';
import { isUnreachable } from '../../utils/collectionRecord';
import { can } from '../../utils/permissions';
import { ALL_VIEWS, newBatchPhoto, nextView } from '../../utils/fieldBatches';
import {
  TAG_REFUSALS, asFile, audioKey, blobOfDataUrl, captureFields, goneOf, photoKey, storedOf,
} from '../../utils/fieldOutbox';
import { thumbnailOf } from '../../utils/fieldFrame';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { exifFromFile } from '../../utils/exifGps';
import { isSpecimenCode } from '../../utils/specimenCode';
import { formatDuration, listAudioNotes, mayRecordAudio, uploadAudioNote } from '../../utils/audioNotes';
import VoiceNoteRecorder from '../VoiceNoteRecorder';
import PhotoThumb from '../PhotoThumb';
import FieldScreen from './FieldScreen';
import FieldDetailsForm from './FieldDetailsForm';
import FieldGonePanel from './FieldGonePanel';
import AiFieldSuggestion from './AiFieldSuggestion';
import { mayIdentifyRecord, mayRequestAi } from '../../utils/aiIdentify';
import { useBackLabels, useBackToGallery } from './useFieldNav';
import { attentionText, coordText, formatMoment, pageStyles as ps, refusalReason } from './fieldFormat';
import CollectionShare from '../share/CollectionShare';
import HandoffPanel from '../handoffs/HandoffPanel';
import { rareFindOf } from '../../utils/handoffs';
import { hereOf, thermalPath } from '../workbench/thermal/links';

// What the collector may still change on their own collection once it is on
// the server (PUT /collections/{id}, the owner's detail fields).
const DETAIL_FIELDS = ['general_notes', 'host_species', 'substrate', 'given_name'];

// The identification shown is the newest one logged against the record, else
// the one it was created with: logging one (POST .../identifications) adds to
// its history and leaves current_taxon_name as it was, so a form refilled from
// that alone would put the old name back the moment a new one was saved.
const fromRecord = (rec, latest) => ({
  given_name: rec?.given_name || '',
  general_notes: rec?.general_notes || '',
  host_species: rec?.host_species || '',
  substrate: rec?.substrate || '',
  taxon_name: (latest ? latest.taxon_name : rec?.current_taxon_name) || '',
  rank: (latest ? latest.taxonomic_rank : rec?.current_rank) || '',
  confidence: (latest ? latest.confidence : rec?.current_confidence) || '',
});

const fromBatch = batch => ({
  given_name: batch?.details?.given_name || '',
  general_notes: batch?.details?.general_notes || '',
  host_species: batch?.details?.host_species || '',
  substrate: batch?.details?.substrate || '',
  taxon_name: batch?.details?.taxon_name || '',
  rank: batch?.details?.rank || '',
  confidence: batch?.details?.confidence || '',
});

const trimmed = v => String(v ?? '').trim();

// The ranks an accepted AI name is recorded at (routes/ai.py LINK_RANKS).
const LINK_RANKS = ['species', 'genus', 'family'];
// What a batch held back by failed uploads can be sent without, by its reason.
const SKIPS = { photos: 'editor.skipPhotos', audio: 'editor.skipAudio', thermal: 'editor.skipThermal' };
const SKIP_TIPS = { photos: 'tip.editor.skipPhotos', audio: 'tip.editor.skipAudio', thermal: 'tip.editor.skipThermal' };

/** The acceptance to record through the AI's own link, or null: only while
 *  the identification being saved is still the name and rank accepted, from
 *  the person's own answer (only its asker may link it). */
export function acceptanceToLink(ai, details) {
  const accepted = ai?.accepted;
  if (!ai?.aiId || !accepted || ai.mine === false) return null;
  const same = trimmed(details.taxon_name).toLowerCase() === trimmed(accepted.taxon_name).toLowerCase()
    && details.rank === accepted.rank;
  if (!same || !LINK_RANKS.includes(details.rank)) return null;
  return { taxon_name: accepted.taxon_name, rank: details.rank, confidence: details.confidence || 'uncertain' };
}

/* A small copy of a photo for the gallery's cards (photos.thumb_key), as the
 * outbox makes one. A browser that cannot decode the photo says so, or says
 * nothing: Save is never held up by it for more than a few seconds. */
async function smallCopyOf(file) {
  let gaveUp;
  const made = await Promise.race([
    Promise.resolve().then(() => thumbnailOf(file)).catch(() => null),
    new Promise((done) => { gaveUp = setTimeout(() => done(null), 5 * 1000); }),
  ]);
  clearTimeout(gaveUp);
  return blobOfDataUrl(made);
}

/**
 * A collection the server has: its code, date and place are shown and not
 * edited -- they are written into its permanent specimen code, and a
 * correction with a reason is made on the specimen's own page by someone who
 * may correct specimens (or, for a record with no code, by a collection
 * manager). Notes, the host, the substrate and the identification can still
 * change, and photos can be added. A batch of this phone's still on its way
 * says how that is going, and asks when it needs its collector; photos it was
 * sent without are still on the phone, and can be sent from here.
 *
 * A photo added here goes up the way the outbox sends one: with what its EXIF
 * says of when and where it was taken as its tags, again without them if the
 * server refuses those (and says so), under an idempotency key, with its
 * small copy.
 *
 * The thermal files the outbox sent with it (its Thermal step) open, for a lab
 * reader, in the analysis workbench's thermal mode, whose Back comes here
 * (2026-10-26; before, only the isolate's dossier opened them). One that is
 * still waiting has no capture yet: it shows nowhere here.
 */
export default function FieldSentEditor({ sampleId, batch, store, outbox, userId, user }) {
  const { t } = useTranslation(['field', 'voice', 'ai', 'thermal']);
  const location = useLocation();
  const back = useBackToGallery();
  const backLabels = useBackLabels();
  const [load, setLoad] = useState('loading');
  // What the server's 404 said of a record that is not there (goneOf).
  const [gone, setGone] = useState(null);
  // The AI's answers about this record, newest first, and the one the person
  // is working with now ({aiId, accepted, mine, ...}, AiFieldSuggestion).
  const [aiAnswers, setAiAnswers] = useState([]);
  const [ai, setAi] = useState(null);
  const [record, setRecord] = useState(null);
  // The rare find in an AI answer asked here (B6): the handoff panel prompts.
  const [rareFind, setRareFind] = useState(null);
  const askedRarity = useRef(null);
  const heardAi = (result) => {
    if (!result?.ai_id || askedRarity.current === result.ai_id) return;
    askedRarity.current = result.ai_id;
    rareFindOf(result, user, { config: { keepSession: true } }).then((found) => {
      if (askedRarity.current === result.ai_id) setRareFind(found);
    });
  };
  const [serverPhotos, setServerPhotos] = useState([]);
  // The record's voice notes (GET /collections/{id}/audio-notes), and new
  // ones recorded here, sent on Save.
  const [serverAudio, setServerAudio] = useState([]);
  const [audioFailed, setAudioFailed] = useState(false);
  const [addedAudio, setAddedAudio] = useState([]);
  const [leftAudio, setLeftAudio] = useState([]);
  const [specimenId, setSpecimenId] = useState(batch?.specimenId || null);
  const [details, setDetails] = useState(() => fromBatch(batch));
  const [original, setOriginal] = useState(() => fromBatch(batch));
  const [added, setAdded] = useState([]);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  // Picked files whose EXIF is still being read: Save waits for them, or they
  // would be left out of a save tapped meanwhile.
  const [reading, setReading] = useState(0);
  const [leftBehind, setLeftBehind] = useState([]);
  const [sendingLeft, setSendingLeft] = useState(false);
  const fileInput = useRef(null);
  const urls = useRef([]);
  // Where an added photo's file was put in storage, by its id: a retry
  // registers the same file, so the same request goes under its key.
  const stored = useRef({});

  const reload = async () => {
    // An expired token is a 401, and a 401 signs the person out.
    if (!outbox.signedIn() || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      setLoad('offline');
      return;
    }
    try {
      const { data } = await api.get(`/collections/${sampleId}`);
      // Newest first; none, or no answer, leaves the record's own.
      const history = await api.get(`/collections/${sampleId}/identifications`).catch(() => null);
      const latest = Array.isArray(history?.data) ? history.data[0] || null : null;
      setRecord(data);
      setDetails(fromRecord(data, latest));
      setOriginal(fromRecord(data, latest));
      setLoad('ok');
      setGone(null);
      if (batch?.gone) {
        // Back on the server (restored, perhaps by someone else): the card
        // stops saying it is gone.
        store.update(userId, batch.id, current => ({
          ...current, gone: undefined, ...(current.attention?.reason === 'gone' ? { attention: null } : {}),
        })).catch(() => {});
      }
      const photos = await api.get(`/collections/${sampleId}/photos`).catch(() => ({ data: [] }));
      setServerPhotos(Array.isArray(photos.data) ? photos.data : []);
      reloadAudio();
      if (mayRequestAi(user) && mayIdentifyRecord(user, data)) reloadAi();
      // The code shown is the server's: a specimen's, linked on site or later
      // (an observation given one on the map), else the record's own QR.
      const shown = data.code || data.qr_uid;
      if (!batch?.specimenId && isSpecimenCode(shown)) {
        // /lookup resolves a specimen code to the specimen first.
        const found = await api.get('/lookup', { params: { code: shown } }).catch(() => null);
        if (found?.data?.type === 'specimen' && found.data.id) setSpecimenId(found.data.id);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (isUnreachable(e)) setLoad('offline');
      else if (status === 404) {
        const found = goneOf(e);
        setGone(found);
        setLoad('missing');
        if (batch && (batch.gone?.deleted !== found.deleted || !batch.gone)) {
          // The gallery's card says so too. A sent batch stays 'sent' (it is
          // not unsent again); one still on its way waits for its collector.
          store.update(userId, batch.id, current => ({
            ...current,
            gone: found,
            ...(current.status === 'sent' ? {} : { status: 'attention', attention: { reason: 'gone', ...found } }),
          })).catch(() => {});
        }
      }
      else if (status === 403) setLoad('forbidden');
      else setLoad('failed');
    }
  };

  // An older server has no such list: then there is just no earlier answer.
  function reloadAi() {
    return api.get(`/collections/${sampleId}/ai-identifications`)
      .then(r => setAiAnswers(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setAiAnswers([]));
  }

  function reloadAudio() {
    return listAudioNotes('collections', sampleId)
      .then((list) => { setServerAudio(list); setAudioFailed(false); })
      .catch(() => setAudioFailed(true));
  }

  const deleteServerAudio = async (note) => {
    setMessage(null);
    try {
      await api.delete(`/audio-notes/${note.audio_id}`);
    } catch {
      setMessage({ tone: 'warn', text: t('voice:deleteFailed') });
    }
    reloadAudio();
  };

  useEffect(() => { reload(); }, [sampleId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Submitted by the outbox while this page was open: the record is read
  // again, so what goes by it being a draft (the handoff panel's request)
  // goes by the server's word. The record alone: the form keeps its edits.
  const batchSent = batch?.status === 'sent';
  const wasSent = useRef(batchSent);
  useEffect(() => {
    const became = batchSent && !wasSent.current;
    wasSent.current = batchSent;
    if (!became || !outbox.signedIn() || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    api.get(`/collections/${sampleId}`).then(({ data }) => setRecord(data)).catch(() => {});
  }, [batchSent, sampleId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // One array for the screen's life, pushed to: the same one at unmount.
    const made = urls.current;
    return () => made.forEach(u => URL.revokeObjectURL(u));
  }, []);

  // Photos this record was sent without, still on the phone: the outbox
  // keeps their files until they are sent from here, or let go.
  const skippedIds = (batch?.photos || []).filter(p => p.skipped && !p.lost && !p.sent).map(p => p.id).join(',');
  useEffect(() => {
    let alive = true;
    (async () => {
      const left = [];
      for (const p of (batch?.photos || []).filter(x => x.skipped && !x.lost && !x.sent)) {
        const blob = await store.getBlob(userId, p.id).catch(() => null);
        if (blob) left.push(p);
      }
      if (alive) setLeftBehind(left);
    })();
    return () => { alive = false; };
  }, [skippedIds, store, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Voice notes it was sent without, likewise.
  const skippedAudioIds = (batch?.audioNotes || []).filter(a => a.skipped && !a.lost && !a.sent).map(a => a.id).join(',');
  useEffect(() => {
    let alive = true;
    (async () => {
      const left = [];
      for (const a of (batch?.audioNotes || []).filter(x => x.skipped && !x.lost && !x.sent)) {
        const blob = await store.getBlob(userId, a.id).catch(() => null);
        if (blob) left.push({ ...a, blob });
      }
      if (alive) setLeftAudio(left);
    })();
    return () => { alive = false; };
  }, [skippedAudioIds, store, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = async (event) => {
    const chosen = [...(event.target.files || [])];
    event.target.value = '';
    if (!chosen.length) return;
    // When and where each file says it was taken, if it says.
    setReading(n => n + 1);
    let metas;
    try {
      metas = await Promise.all(chosen.map(f => exifFromFile(f).catch(() => null)));
    } finally {
      setReading(n => n - 1);
    }
    const fresh = chosen.map((file, i) => {
      const photo = {
        ...newBatchPhoto({ view: 'other', takenAt: metas[i]?.takenAt || null, fix: metas[i]?.gps || null }), file,
      };
      if (typeof URL.createObjectURL === 'function') {
        photo.preview = URL.createObjectURL(file);
        urls.current.push(photo.preview);
      }
      return photo;
    });
    // Each filed under the first view neither the record nor these have.
    setAdded((list) => {
      const taken = [...serverPhotos.map(p => ({ view: p.photo_type })), ...list];
      return fresh.reduce((next, photo) => {
        const filed = { ...photo, view: nextView(taken) };
        taken.push(filed);
        return [...next, filed];
      }, list);
    });
  };

  // Going on without what failed lets go of the photos and the voice notes
  // not yet on the record alike: both stay on the phone, to send from here.
  const retry = (skipFailed = false) => store.update(userId, batch.id, current => ({
    ...current,
    status: 'pending',
    attention: null,
    photoPasses: 0,
    serverErrors: 0,
    photos: skipFailed
      ? (current.photos || []).map(p => (p.sent ? p : { ...p, skipped: true }))
      : current.photos,
    audioNotes: skipFailed
      ? (current.audioNotes || []).map(a => (a.sent ? a : { ...a, skipped: true }))
      : current.audioNotes,
    thermal: skipFailed
      ? (current.thermal || []).map(f => (f.sent ? f : { ...f, skipped: true }))
      : current.thermal,
  })).then(() => outbox.run());

  /** The photos left behind, sent to the record now. Each goes under the key
   *  the outbox would have used, so one it registered already is not sent
   *  twice. */
  const sendLeftBehind = async () => {
    if (!outbox.signedIn()) return setMessage({ tone: 'warn', text: t('editor.offline') });
    setSendingLeft(true);
    setMessage(null);
    let failed = 0;
    const key = batch.createKey || `field-${batch.id}`;
    for (const photo of leftBehind) {
      const blob = await store.getBlob(userId, photo.id).catch(() => null);
      if (!blob) continue;
      const index = (batch.photos || []).findIndex(p => p.id === photo.id);
      const file = asFile(blob, photo, index);
      const tags = captureFields(photo);
      const send = tagged => uploadOnePhoto(
        { file, photoType: photo.view, notes: photo.note || '' },
        { sample_id: sampleId, ...(tagged ? tags : {}) },
        trimmed(details.taxon_name), 'field_collection',
        { idempotencyKey: photoKey(key, photo.id, tagged), requestConfig: { keepSession: true },
          stored: photo.s3_key ? storedOf(photo) : null },
      );
      let result = await send(true);
      const untagged = !result.ok && TAG_REFUSALS.has(result.code) && Object.keys(tags).length > 0;
      if (untagged) result = await send(false);
      if (!result.ok) {
        failed += 1;
        continue;
      }
      await store.update(userId, batch.id, current => ({
        ...current,
        photos: (current.photos || []).map(p => (p.id === photo.id
          ? { ...p, sent: true, skipped: false, ...(untagged ? { tagsRefused: true } : {}) } : p)),
      }));
      await store.removeBlobs(userId, [photo.id]);
    }
    let audioFailures = 0;
    for (const note of leftAudio) {
      const result = await uploadAudioNote(note, { sample_id: sampleId }, {
        idempotencyKey: audioKey(key, note.id), requestConfig: { keepSession: true },
        stored: note.s3_key ? { s3_key: note.s3_key, s3_url: note.s3_url } : null,
      });
      if (!result.ok) {
        audioFailures += 1;
        continue;
      }
      await store.update(userId, batch.id, current => ({
        ...current,
        audioNotes: (current.audioNotes || []).map(a => (a.id === note.id ? { ...a, sent: true, skipped: false } : a)),
      }));
      await store.removeBlobs(userId, [note.id]);
    }
    if (leftAudio.length) reloadAudio();
    setSendingLeft(false);
    const said = [failed ? t('editor.photosNotAdded', { count: failed }) : '',
      audioFailures ? t('editor.audioNotAdded', { count: audioFailures }) : ''].filter(Boolean);
    setMessage(said.length ? { tone: 'warn', text: said.join(' ') } : { tone: 'ok', text: t('editor.saved') });
    api.get(`/collections/${sampleId}/photos`)
      .then(r => setServerPhotos(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    return undefined;
  };

  /** Let the photos left behind go from the phone, for good. */
  const discardLeftBehind = async () => {
    const ids = [...leftBehind, ...leftAudio].map(p => p.id);
    await store.update(userId, batch.id, current => ({
      ...current,
      photos: (current.photos || []).map(p => (ids.includes(p.id) ? { ...p, lost: true } : p)),
      audioNotes: (current.audioNotes || []).map(a => (ids.includes(a.id) ? { ...a, lost: true } : a)),
    }));
    await store.removeBlobs(userId, ids);
  };

  const save = async () => {
    setMessage(null);
    const changes = {};
    for (const field of DETAIL_FIELDS) {
      if (trimmed(details[field]) !== trimmed(original[field])) changes[field] = trimmed(details[field]) || null;
    }
    const identChanged = ['taxon_name', 'rank', 'confidence']
      .some(k => trimmed(details[k]) !== trimmed(original[k]));
    if (identChanged && (!trimmed(details.taxon_name) || !details.rank)) {
      return setMessage({ tone: 'warn', text: t('editor.identIncomplete') });
    }
    setSaving(true);
    try {
      if (Object.keys(changes).length) await api.put(`/collections/${sampleId}`, changes);
      if (identChanged) {
        // A name accepted from the AI's answer, kept as accepted, is recorded
        // through the answer's link: the server checks it was one of the
        // candidates and nothing dangerous was flagged, and records it as
        // AI-assisted ('ai_image'). Anything else -- the link refused, or an
        // answer already accepted once -- is the person's own identification.
        const accept = acceptanceToLink(ai, details);
        let recorded = false;
        if (accept) {
          try {
            const linked = await api.post(`/ai/identifications/${ai.aiId}/link`, { sample_id: sampleId, accept },
              { headers: { 'Idempotency-Key': `field-sent-${sampleId}-ai-${ai.aiId}` } });
            recorded = !!linked?.data?.identification_id;
          } catch (e) {
            if (isUnreachable(e)) throw e;
          }
        }
        if (!recorded) {
          await api.post(`/collections/${sampleId}/identifications`, {
            taxon_name: trimmed(details.taxon_name),
            taxonomic_rank: details.rank,
            confidence: details.confidence || 'uncertain',
            identification_method: 'field',
          });
        }
      }
      let failed = 0;
      let untagged = 0;
      const went = new Set();
      const key = batch?.createKey || `field-sent-${sampleId}`;
      for (const photo of added) {
        const tags = captureFields(photo);
        // Made only while the file still has to go up.
        const small = stored.current[photo.id] ? null : await smallCopyOf(photo.file);
        const send = tagged => uploadOnePhoto(
          { file: photo.file, photoType: photo.view, notes: photo.note || '' },
          { sample_id: sampleId, ...(tagged ? tags : {}) },
          trimmed(details.taxon_name), 'field_collection',
          {
            // The view is part of the request: changed after a failed Save, it
            // is a new one, not a repeat the idempotency layer answers 422.
            idempotencyKey: photoKey(key, `${photo.id}-${photo.view}`, tagged),
            thumbnail: small,
            stored: stored.current[photo.id] || null,
            onStored: (where) => { stored.current[photo.id] = where; },
          },
        );
        let result = await send(true);
        // Its time or place refused (a phone whose clock runs fast): the
        // photo goes without them rather than not at all.
        const dropped = !result.ok && TAG_REFUSALS.has(result.code) && Object.keys(tags).length > 0;
        if (dropped) result = await send(false);
        if (!result.ok) {
          failed += 1;
          continue;
        }
        if (dropped) untagged += 1;
        went.add(photo.id);
        delete stored.current[photo.id];
      }
      // Only what went leaves the form: a photo picked while this ran stays.
      setAdded(list => list.filter(p => !went.has(p.id)));
      // Then the voice notes recorded here, each under a key of its own.
      let audioFailures = 0;
      const heard = new Set();
      for (const note of addedAudio) {
        const result = await uploadAudioNote(note, { sample_id: sampleId },
          { idempotencyKey: audioKey(key, note.id) });
        if (result.ok) heard.add(note.id);
        else audioFailures += 1;
      }
      if (addedAudio.length) {
        setAddedAudio(list => list.filter(n => !heard.has(n.id)));
        reloadAudio();
      }
      if (batch) {
        // The card on this phone says what the server now does.
        await store.update(userId, batch.id, current => ({
          ...current,
          details: {
            ...current.details,
            given_name: details.given_name,
            general_notes: details.general_notes,
            host_species: details.host_species,
            substrate: details.substrate,
            taxon_name: details.taxon_name,
            rank: details.rank,
            confidence: details.confidence,
          },
        }));
      }
      setOriginal({ ...details });
      const said = [failed ? t('editor.photosNotAdded', { count: failed }) : t('editor.saved')];
      if (untagged) said.push(t('editor.tagsRefused', { count: untagged }));
      if (audioFailures) said.push(t('editor.audioNotAdded', { count: audioFailures }));
      setMessage({ tone: failed || untagged || audioFailures ? 'warn' : 'ok', text: said.join(' ') });
      if (identChanged) setAi(null);
      if (identChanged || Object.keys(changes).length) reload();
      else if (added.length) {
        api.get(`/collections/${sampleId}/photos`)
          .then(r => setServerPhotos(Array.isArray(r.data) ? r.data : []))
          .catch(() => {});
      }
    } catch (e) {
      const status = e?.response?.status;
      let text = t('editor.saveFailed', { why: refusalReason(t, { status, code: e?.response?.data?.code }) });
      if (status === 403) text = t('editor.forbidden');
      else if (isUnreachable(e)) text = t('editor.offline');
      setMessage({ tone: 'warn', text, detail: status && status !== 403 ? e?.response?.data?.error : null });
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const code = record?.code || record?.qr_uid || batch?.serverCode || batch?.specimenCode || '';
  const moment = record?.collection_datetime || batch?.startedAt;
  const lat = record?.gps_lat ?? batch?.location?.lat;
  const lng = record?.gps_lng ?? batch?.location?.lng;
  const place = record?.region || batch?.region || '';
  // A public sighting, once sent, is the reviewers' (routes/collections.py:
  // never edited once sent); its sender reads it here (public portal 2a).
  const sentSighting = !!record?.is_public_submission && record?.is_draft === false;
  const editable = load === 'ok' && !sentSighting;
  const reason = batch?.attention?.reason;
  const changed = DETAIL_FIELDS.concat(['taxon_name', 'rank', 'confidence'])
    .some(k => trimmed(details[k]) !== trimmed(original[k])) || added.length > 0 || addedAudio.length > 0;
  // Who can correct what makes up the code, and whether there is one at all.
  const hasCode = isSpecimenCode(code);
  const canCorrect = can(user, 'specimen:write');
  let fixedHelp = 'editor.fixedHelpNoCode';
  if (hasCode) fixedHelp = canCorrect ? 'editor.fixedHelp' : 'editor.fixedHelpAsk';
  const notApplied = batch?.editsNotApplied || [];
  // Photos the outbox sent without their time and place, the server having
  // refused them: said here, where the record's photos are.
  const tagsDropped = (batch?.photos || []).filter(p => p.tagsRefused).length;
  // Its thermal files on the server, each a capture the workbench opens (lab readers: the thermal routes' own rule).
  const sentThermal = can(user, 'lab:read') ? (batch?.thermal || []).filter(f => f.sent && f.captureId) : [];

  return (
    <FieldScreen
      testid="field-editor"
      title={t('editor.sentTitle')}
      backLabel={t(backLabels[0])}
      backTip={t(backLabels[1])}
      onBack={back}
    >
      <div data-testid="editor-mode" data-mode="sent" hidden />

      {batch && batch.status === 'attention' && reason !== 'gone' && load !== 'missing' && (
        <div style={ps.warn} role="alert" data-testid="editor-attention" data-reason={reason}>
          <p style={{ margin: 0 }}>{attentionText(t, batch.attention)}</p>
          {batch.attention.message && (reason === 'refused' || reason === 'submit') && (
            <p style={{ ...ps.hint, marginTop: 6 }} data-testid="editor-attention-detail">
              <bdi dir="ltr">{batch.attention.message}</bdi>
            </p>
          )}
          <div style={{ ...ps.row, marginTop: 10 }}>
            <button type="button" style={ps.secondary} title={t('tip.editor.retry')} data-testid="editor-retry"
              onClick={() => retry(false)}>
              {t(reason === 'legacy_draft' ? 'editor.sendIt' : 'editor.retry')}
            </button>
            {(reason === 'photos' || reason === 'audio' || reason === 'thermal') && (
              <button type="button" style={ps.linkBtn}
                title={t(SKIP_TIPS[reason])}
                data-testid="editor-skip-photos" onClick={() => retry(true)}>
                {t(SKIPS[reason])}
              </button>
            )}
          </div>
        </div>
      )}
      {batch && (batch.status === 'pending' || batch.status === 'sending') && (
        <p style={ps.note} data-testid="editor-status">
          {t(batch.status === 'sending' ? 'editor.sendingNow' : 'editor.waitingSubmit')}
        </p>
      )}
      {notApplied.length > 0 && (
        <p style={ps.note} data-testid="editor-not-applied">{t('editor.editsNotApplied')}</p>
      )}

      <section style={ps.section} aria-labelledby="sent-fixed" data-testid="editor-fixed">
        <h2 id="sent-fixed" style={ps.h2}>{t('editor.fixedTitle')}</h2>
        {/* Share or export this finding (2026-10-19): Field Mode's own page of it. */}
        {record && load === 'ok' && <CollectionShare collection={record} field testid="editor-share" />}
        <dl style={ss.dl}>
          <dt style={ps.label}>{t('editor.code')}</dt>
          <dd style={{ ...ss.dd, ...ps.mono }} dir="ltr" data-testid="editor-code">{code || (load === 'loading' ? '…' : t('gallery.noCode'))}</dd>
          <dt style={ps.label}>{t('editor.when')}</dt>
          <dd style={ss.dd} data-testid="editor-when-fixed">{formatMoment(moment) || '—'}</dd>
          <dt style={ps.label}>{t('editor.where')}</dt>
          <dd style={ss.dd} data-testid="editor-where-fixed">
            {[place, coordText(lat, lng)].filter(Boolean).join(' · ') || '—'}
          </dd>
        </dl>
        <p style={ps.hint} data-testid="editor-fixed-help" data-help={fixedHelp}>{t(fixedHelp)}</p>
        {hasCode && specimenId && (
          <Link to={`/specimens/${specimenId}`} style={ss.link} data-testid="editor-specimen-link">
            {t(canCorrect ? 'editor.specimenPage' : 'editor.specimenPageView')}
          </Link>
        )}
      </section>

      {load === 'loading' && <p style={ps.hint}>{t('editor.loading')}</p>}
      {load === 'offline' && <p style={ps.note} data-testid="editor-offline">{t('editor.offlineEdit')}</p>}
      {load === 'missing' && batch && (
        <FieldGonePanel sampleId={sampleId} batch={batch} gone={gone} store={store} outbox={outbox}
          userId={userId} user={user} onRestored={reload} onRemoved={back} />
      )}
      {load === 'missing' && !batch && (
        <p style={ps.warn} data-testid="editor-missing-record">
          {gone?.deleted
            ? t('editor.gone.why.deleted', { when: formatMoment(gone.deletedAt) || '—' })
            : t('editor.serverMissing')}
        </p>
      )}
      {load === 'forbidden' && <p style={ps.warn} data-testid="editor-forbidden">{t('editor.forbidden')}</p>}
      {load === 'failed' && <p style={ps.warn}>{t('editor.loadFailed')}</p>}

      {/* Gone from the server: its panel above holds the phone's copy, and
          nothing here can be saved to a record that is not there. */}
      {load !== 'missing' && (<>
      <FieldDetailsForm value={details} onChange={setDetails} sent disabled={!editable || saving}
        canAsk={outbox.signedIn} />

      {editable && mayRequestAi(user) && mayIdentifyRecord(user, record) && (
        <section style={ps.section} aria-labelledby="sent-ai" data-testid="editor-ai">
          <h2 id="sent-ai" style={ps.h2}>{t('ai:field.title')}</h2>
          {/* The record's own photographs, by id: nothing goes up again. */}
          <AiFieldSuggestion
            photos={serverPhotos.map(p => ({ photoId: p.photo_id, view: p.photo_type }))}
            latest={aiAnswers[0] || null}
            user={user}
            value={ai}
            onChange={setAi}
            hadOwnName={!!trimmed(original.taxon_name)}
            disabled={saving}
            keptKey="field.keptRecord"
            testid="editor-ai-suggest"
            onResult={heardAi}
            onAccept={chosen => setDetails(d => ({
              ...d, taxon_name: chosen.taxon_name, rank: chosen.rank, confidence: d.confidence || 'uncertain',
            }))}
          />
          {added.length > 0 && <p style={ps.hint} data-testid="editor-ai-save-first">{t('ai:field.saveNewPhotosFirst')}</p>}
        </section>
      )}

      {/* Handing the find to the lab (B6): its request, invitation or status. */}
      {/* A sent public sighting is read only, yet its sender may still hand it in
          (public portal 2d): the panel follows the record being loaded. */}
      {load === 'ok' && (
        <HandoffPanel sampleId={sampleId} record={record} batch={batch} store={store} userId={userId} user={user}
          find={rareFind} />
      )}

      {(leftBehind.length > 0 || leftAudio.length > 0) && batch?.serverSampleId && (
        <section style={ps.section} aria-labelledby="sent-left" data-testid="editor-left-behind">
          <h2 id="sent-left" style={ps.h2}>
            {[leftBehind.length ? t('editor.leftBehindTitle', { count: leftBehind.length }) : '',
              leftAudio.length ? t('editor.leftBehindAudio', { count: leftAudio.length }) : '']
              .filter(Boolean).join(' · ')}
          </h2>
          <p style={ps.hint}>{t('editor.leftBehindHelp')}</p>
          <div style={ps.row}>
            <button type="button" style={ps.secondary} title={t('tip.editor.sendLeftBehind')}
              data-testid="editor-send-left-behind" disabled={!editable || sendingLeft} onClick={sendLeftBehind}>
              {sendingLeft ? t('editor.saving') : t('editor.sendLeftBehind')}
            </button>
            <button type="button" style={ps.linkBtn} title={t('tip.editor.discardLeftBehind')}
              data-testid="editor-discard-left-behind" disabled={sendingLeft} onClick={discardLeftBehind}>
              {t('editor.discardLeftBehind')}
            </button>
          </div>
        </section>
      )}

      <fieldset style={ps.section} disabled={!editable || saving} data-testid="editor-photos">
        <legend style={{ ...ps.h2, padding: 0 }}>{t('editor.photos')}</legend>
        {tagsDropped > 0 && (
          <p style={ps.hint} data-testid="editor-tags-refused">{t('editor.tagsRefused', { count: tagsDropped })}</p>
        )}
        {serverPhotos.length > 0 && (
          <ul style={ss.grid}>
            {serverPhotos.map((p, i) => (
              <li key={p.photo_id} style={ss.cell}>
                {/* Opens full size, to pinch and move (PhotoThumb). */}
                <PhotoThumb photos={serverPhotos} index={i} style={{ width: '100%', height: '100%' }}
                  testId={`editor-photo-open-${i}`}
                  label={t('camera.trayPhoto', { n: i + 1, view: t(`views.${p.photo_type}`, { defaultValue: p.photo_type }) })}>
                  {/* Re-based: from the desktop these name 127.0.0.1 -- the phone, on a phone. */}
                  <img src={resolvePhotoUrl(p.thumbnail_url || p.s3_url)} alt={t(`views.${p.photo_type}`, { defaultValue: p.photo_type })}
                    style={ss.img} loading="lazy" decoding="async" />
                </PhotoThumb>
              </li>
            ))}
          </ul>
        )}
        {added.map((p, i) => (
          <div key={p.id} style={ss.added} data-testid={`editor-added-${i}`}>
            {p.preview && <img src={p.preview} alt="" style={{ ...ss.img, width: 56, height: 56 }} />}
            <select style={{ ...ps.input, flex: 1 }} value={p.view} title={t('tip.editor.view')}
              aria-label={t('editor.view', { n: i + 1 })} data-testid={`editor-added-view-${i}`}
              onChange={e => setAdded(list => list.map(x => (x.id === p.id ? { ...x, view: e.target.value } : x)))}>
              {ALL_VIEWS.map(v => <option key={v} value={v}>{t(`views.${v}`)}</option>)}
            </select>
            <button type="button" style={ps.secondary} title={t('tip.editor.removePhoto')}
              aria-label={t('camera.removePhoto', { n: i + 1, view: t(`views.${p.view}`) })}
              onClick={() => setAdded(list => list.filter(x => x.id !== p.id))}>
              ×
            </button>
          </div>
        ))}
        <button type="button" style={{ ...ps.secondary, alignSelf: 'flex-start' }} title={t('tip.editor.addPhotos')}
          data-testid="editor-add-photos-button" onClick={() => fileInput.current?.click()}>
          {t('editor.addPhotos')}
        </button>
        <input ref={fileInput} type="file" accept="image/*" multiple style={{ display: 'none' }} tabIndex={-1}
          aria-hidden="true" data-testid="editor-add-photos" onChange={addFiles} />
      </fieldset>

      {sentSighting && (
        <p role="note" style={ps.hint} data-testid="editor-sent-sighting">{t('editor.sentSighting')}</p>
      )}
      {mayRecordAudio(user) && (
      <fieldset style={ps.section} disabled={!editable || saving} data-testid="editor-audio">
        <legend style={{ ...ps.h2, padding: 0 }}>{t('voice:onRecord')}</legend>
        {audioFailed && <p style={ps.hint}>{t('voice:loadFailed')}</p>}
        {!audioFailed && serverAudio.length === 0 && editable && (
          <p style={ps.hint} data-testid="editor-audio-none">{t('voice:noneYet')}</p>
        )}
        {serverAudio.length > 0 && (
          <ol style={ss.audioList}>
            {serverAudio.map((n, i) => (
              <li key={n.audio_id} style={ss.audioItem} data-testid={`editor-audio-${i}`}>
                <span style={ps.label}>{t('voice:noteLabel', { n: i + 1 })}</span>
                {/* Re-based like the photos: a desktop names 127.0.0.1 -- the phone, on a phone. */}
                <audio controls preload="none" src={resolvePhotoUrl(n.s3_url)} style={ss.audio}
                  aria-label={t('voice:noteLabel', { n: i + 1 })} />
                <span style={ps.hint}>
                  {Number.isFinite(n.duration_s) ? `${formatDuration(n.duration_s)} · ` : ''}
                  {t('voice:byOn', { who: n.uploaded_by_name || '—', when: formatMoment(n.recorded_at || n.uploaded_at) })}
                </span>
                {n.can_delete && (
                  <button type="button" style={ps.linkBtn} title={t('voice:tip.deleteSaved')}
                    data-testid={`editor-audio-delete-${i}`} onClick={() => deleteServerAudio(n)}>
                    {t('voice:deleteSaved')}
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
        <VoiceNoteRecorder value={addedAudio} onChange={setAddedAudio} disabled={!editable || saving}
          testId="editor-voice" />
      </fieldset>
      )}

      {sentThermal.length > 0 && (
        <section style={ps.section} aria-labelledby="sent-thermal" data-testid="editor-thermal-sent">
          <h2 id="sent-thermal" style={ps.h2}>{t('thermal:field.title')}</h2>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sentThermal.map(f => (
              <li key={f.id}>
                <Link to={thermalPath({ capture: f.captureId, back: hereOf(location) })} style={ss.link}
                  data-testid={`editor-thermal-open-${f.captureId}`}>
                  {t('thermal:field.analyse', { name: f.name || t('thermal:field.unnamed') })}
                </Link>
                {f.decoded === false && <span style={ps.hint}> · {t('thermal:field.sentUndecoded')}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {message && (
        <div role={message.tone === 'ok' ? 'status' : 'alert'}>
          <p style={message.tone === 'ok' ? ps.ok : ps.warn} data-testid="editor-message">
            {message.text}
          </p>
          {message.detail && (
            <p style={{ ...ps.hint, marginTop: 6 }}><bdi dir="ltr">{message.detail}</bdi></p>
          )}
        </div>
      )}
      <div style={ps.footer}>
        <button type="button" style={{ ...ps.primary, width: '100%', opacity: editable && changed && !saving && !reading ? 1 : 0.5 }}
          disabled={!editable || !changed || saving || reading > 0} title={t('tip.editor.saveSent')} data-testid="editor-save"
          onClick={save}>
          {saving ? t('editor.saving') : t('editor.save')}
        </button>
      </div>
      </>)}
    </FieldScreen>
  );
}

const ss = {
  dl: { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 12px', margin: 0 },
  dd: { margin: 0, fontSize: 15, color: 'var(--c-text-2-fg)', overflowWrap: 'anywhere' },
  grid: {
    listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8,
    gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
  },
  cell: { aspectRatio: '1 / 1', borderRadius: 8, overflow: 'hidden', background: 'var(--c-bg-subtle-6)' },
  img: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 8 },
  added: { display: 'flex', alignItems: 'center', gap: 10 },
  audioList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  audioItem: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  audio: { flex: '1 1 200px', minWidth: 0, maxWidth: '100%', height: 36 },
  link: {
    display: 'inline-flex', alignItems: 'center', minHeight: 44, alignSelf: 'flex-start',
    color: 'var(--c-info-2-fg)', fontSize: 15, fontWeight: 600,
  },
};
